import { isApiError } from 'services/api.types';
import {
  $completeSignIn,
  $forgetSession,
  $loadProfile,
  $restoreSession,
  $signIn,
  $signOut,
  AuthTypes,
} from 'services/auth';
import { tokens } from 'services/tokens';
import { BLoCBase } from 'types/BLoCBase';

/**
 * The app's access control. Anyone Cognito lets sign in may use the tool, so it is
 * signed in or not — plus the "we do not know yet" state the router needs on a cold load:
 *
 * - `loading`      — undecided. The app shows a splash, never the login page.
 * - `unauthorized` — no session. Only `PublicModule` renders.
 * - `authorized`   — signed in. The app shell renders.
 */
export type UserState = 'loading' | 'authorized' | 'unauthorized';

type State = {
  userState: UserState;
  user: AuthTypes.User | null;
  signingIn: boolean;
  signInError: string | null;
  /** The deep link a completed sign-in returns to, carried through the IDP round trip. */
  returnTo: string | null;
};

const SIGN_IN_FAILED = 'Sign-in did not complete. Try again.';
const SESSION_EXPIRED = 'Your session has expired. Sign in again.';

const messageFrom = (err: unknown) => (isApiError(err) ? err.message : SIGN_IN_FAILED);

/** A failure past the point of no return has nobody left to tell; it must not surface as unhandled. */
const ignore = () => {};

/**
 * The app-wide session: which state the app is in, who the user is, and the actions that
 * move between them. The IDP itself is behind `services/auth`.
 */
class UserStore extends BLoCBase<State> {
  public $userState = this.$getState('userState');
  public $user = this.$getState('user');
  /** In flight at the IDP. What the sign-in button's pending state binds to. */
  public $signingIn = this.$getState('signingIn');
  public $signInError = this.$getState('signInError');
  public $returnTo = this.$getState('returnTo');

  /**
   * Restore starts here rather than in an `onInit`, because a global store has no
   * lifecycle to hang one on: the first thing that reads `$userState` has to already see
   * `loading` with the answer on its way.
   *
   * Every phase of the session shares the `session` subscription key, so starting one
   * cancels whatever the last one left in flight — they are mutually exclusive by nature,
   * and a stale profile call resolving late must never revive a session the user left.
   */
  constructor() {
    super({ userState: 'loading', user: null, signingIn: false, signInError: null, returnTo: null });

    this.addSub(
      $restoreSession().subscribe({
        next: (credentials) => (credentials ? this.establish(credentials) : this.clearSession()),
        error: () => this.clearSession(),
      }),
      'session'
    );

    /** Inactivity is the user walking away, so it is a full sign-out; a refused refresh is not. */
    this.addSub(
      tokens.$sessionEnded.subscribe((reason) => (reason === 'idle' ? this.logout() : this.expire())),
      'sessionEnded'
    );
  }

  /**
   * The user asked to sign in, from the login page, which knows the deep link they were
   * headed for. The page is left for the IDP, so `userState` stays `unauthorized`: the
   * login page has to stay mounted to show its own pending state until it unloads, and a
   * redirect that cannot start has to land back on it.
   */
  public login = (returnTo: string | null = null) => {
    if (this.currentState('signingIn')) return;

    this.setStates({ signingIn: true, signInError: null });

    this.addSub(
      $signIn(returnTo).subscribe({
        error: (err) => this.setStates({ signingIn: false, signInError: messageFrom(err) }),
      }),
      'session'
    );
  };

  /**
   * The IDP redirected back to the callback route. A refusal lands back on the login page
   * with the reason, through the same `signInError` a failed `login` uses.
   *
   * Guarded like `login`, because an authorization code can only be exchanged once — and
   * React's strict mode runs the callback page's effect twice.
   */
  public completeSignIn = () => {
    if (this.currentState('signingIn')) return;

    this.setStates({ signingIn: true, signInError: null });

    this.addSub(
      $completeSignIn().subscribe({
        next: ({ credentials, returnTo }) => {
          this.setStates({ signingIn: false, returnTo });
          this.establish(credentials);
        },
        error: (err) => this.setStates({ signingIn: false, signInError: messageFrom(err) }),
      }),
      'session'
    );
  };

  /**
   * Credentials say nothing about who the user is, so the app sits on the splash until the
   * profile call answers rather than rendering a shell with nobody in it.
   */
  private establish = (credentials: AuthTypes.Credentials) => {
    tokens.start(credentials);
    this.setState('userState', 'loading');

    this.addSub(
      $loadProfile().subscribe({
        next: (user) => this.setStates({ user, userState: 'authorized' }),
        /**
         * We do not know who this is, so the session is unusable. Back to the login page
         * carrying the reason, rather than bouncing there silently.
         */
        error: (err) => {
          this.clearSession();
          this.setState('signInError', messageFrom(err));
        },
      }),
      'session'
    );
  };

  /** Drops everything the session owned in memory and lands the app back on the login page. */
  private clearSession = () => {
    this.unsub('session');
    tokens.clear();
    this.setStates({
      userState: 'unauthorized',
      user: null,
      signingIn: false,
      signInError: null,
      returnTo: null,
    });
  };

  /** The session is gone at the IDP too, so drop what this browser stored of it. */
  private forgetSession = () => {
    this.clearSession();
    this.addSub($forgetSession().subscribe({ error: ignore }), 'session');
  };

  /** The IDP refused to refresh the session: back to the login page, saying why. */
  private expire = () => {
    this.forgetSession();
    this.setState('signInError', SESSION_EXPIRED);
  };

  /**
   * Called by `services/api` on any `401`. Separate from `logout` because it is not a
   * user action: the session ended underneath us, and the app has to reflect that
   * wherever it happens to be. Local only — there is no IDP session to end that would
   * not just sign the user back in to the same rejection.
   */
  public setUnauthorized = () => this.forgetSession();

  /**
   * The user's own way out, and where inactivity ends up. Local state goes first, so
   * nothing authenticated is left on screen — or in the back/forward cache — while the
   * browser leaves for the IDP's `/logout`. Without that step Cognito's own session cookie
   * would sign the user straight back in.
   */
  public logout = () => {
    this.clearSession();
    this.addSub($signOut().subscribe({ error: ignore }), 'session');
  };
}

let store: Readonly<UserStore> | null = null;

export const getUserStore = () => {
  if (!store) {
    store = Object.freeze(new UserStore());
  }
  return store;
};
